import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Modal } from "@/components/ui/Modal";

export type SubmissionImageComparisonProps = {
  originalImageUrl?: string | null;
  correctionImageUrl?: string | null;
  originalLabel?: string;
  correctionLabel?: string;
  className?: string;
};

type LoadPhase = "loading" | "loaded" | "error";

function trimUrl(u: string | null | undefined): string {
  return typeof u === "string" ? u.trim() : "";
}

const ImagePanel: React.FC<{
  title: string;
  url: string;
  openLabel?: string;
}> = ({ title, url, openLabel = "Open image in new tab" }) => {
  const [phase, setPhase] = useState<LoadPhase>("loading");
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setPhase("loading");
    setLightboxOpen(false);
  }, [url]);

  return (
    <div className="flex flex-col gap-2 min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </p>
      <div className="relative rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 overflow-hidden min-h-[12rem] flex items-center justify-center">
        {phase === "loading" && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-50/90 dark:bg-slate-900/60">
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading image…</p>
          </div>
        )}
        {phase === "error" && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 p-4 text-center bg-slate-50 dark:bg-slate-900/40">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              This image could not be displayed.
            </p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3.5 h-3.5 shrink-0" aria-hidden />
              Try opening in a new tab
            </a>
          </div>
        )}
        {phase !== "error" && (
          <button
            type="button"
            disabled={phase !== "loaded"}
            className="group relative w-full max-w-full rounded-lg border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 dark:focus-visible:ring-offset-slate-900 enabled:cursor-pointer disabled:cursor-default"
            onClick={() => setLightboxOpen(true)}
            aria-label={`${title}: tap or click to view full size`}
          >
            <img
              key={url}
              src={url}
              alt={title}
              onLoad={() => setPhase("loaded")}
              onError={() => setPhase("error")}
              className={`max-w-full w-full h-auto max-h-[min(50vh,28rem)] object-contain block mx-auto transition-opacity duration-150 ${
                phase === "loaded" ? "opacity-100" : "opacity-0"
              }`}
              draggable={false}
            />
            {phase === "loaded" && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent pt-10 pb-2 px-3">
                <p className="text-center text-xs font-medium text-white drop-shadow-sm sm:text-sm">
                  Tap to view
                </p>
              </div>
            )}
          </button>
        )}
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1 w-fit"
      >
        <ExternalLink className="w-3.5 h-3.5 shrink-0" aria-hidden />
        {openLabel}
      </a>

      <Modal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        title={title}
        size="xl"
        showCloseButton
      >
        <div className="flex flex-col gap-3">
          <p className="text-center text-sm font-medium text-slate-700 dark:text-slate-200">
            Tap to view
          </p>
          <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-center min-h-[40vh] max-h-[min(75vh,40rem)] overflow-hidden">
            <img
              src={url}
              alt={title}
              className="max-h-[min(75vh,40rem)] w-full object-contain"
            />
          </div>
          <p className="text-center text-xs text-slate-500 dark:text-slate-400">
            Tap outside the window or use Close to go back
          </p>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center justify-center gap-1.5"
          >
            <ExternalLink className="w-4 h-4 shrink-0" aria-hidden />
            Open image in new tab
          </a>
        </div>
      </Modal>
    </div>
  );
};

/**
 * Side-by-side (md+) or stacked original vs teacher-corrected submission images.
 * Renders nothing when both URLs are missing.
 */
export const SubmissionImageComparison: React.FC<SubmissionImageComparisonProps> = ({
  originalImageUrl,
  correctionImageUrl,
  originalLabel = "Original submission",
  correctionLabel = "Teacher correction",
  className = "",
}) => {
  const original = trimUrl(originalImageUrl);
  const correction = trimUrl(correctionImageUrl);

  if (!original && !correction) return null;

  /** Show teacher-corrected image first when present (student-facing priority). */
  return (
    <div
      className={`grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 md:items-start ${className}`.trim()}
    >
      {correction ? <ImagePanel title={correctionLabel} url={correction} /> : null}
      {original ? <ImagePanel title={originalLabel} url={original} /> : null}
    </div>
  );
};
